import { useMutation, useQueryClient } from "@tanstack/react-query";
import { calculateRefundAmount, updateRescheduleStatus } from "api/booking.api";
import { useToast } from "hooks";
import { useState } from "react";
import styled from "styled-components";
import { UpcomingClassCardInstructor } from "ui/base/class/UpcomingClassCardInstructor";
import { NoContent } from "ui/base/NoContent/NoContent";
import { hideScrollBar } from "ui/base/scrollbarStyle/scrollbarStyle";
import NoUpcomingClassesFound from "ui/icons/not-found-icons/noUpcoming.svg";
import { fonts } from "ui/theme";
import { CancellationModal } from "./CancellationModal";
import { DeclineModal } from "./DeclineModal";
import { UserProfileModal } from "ui/base/class/UserProfileModal";

export const UpcomingClasses = ({ futureClasses, onSendMessage }) => {
    const queryClient = useQueryClient();
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [showUserModal, setShowUserModal] = useState(false);
    const [showDeclineModal, setShowDeclineModal] = useState(false);
    const [cancellationData, setCancellationData] = useState(null);
    const [selectedBooking, setSelectedBooking] = useState(null);

    const { mutate: getRefundAmount } = useMutation({
        mutationFn: calculateRefundAmount,
        onError: () => useToast("error", "Refund data can not load"),
        onSuccess: (data) => {
            setCancellationData(data);
            setShowDeclineModal(true);
        },
    });

    const { mutate } = useMutation({
        mutationFn: updateRescheduleStatus,
        onError: () => useToast("error", "Re-schedule has been failed"),
        onSuccess: () => {
            useToast("success", "Re-schedule has been updated");
            queryClient.invalidateQueries("getNumberOfRescheduleRequestForTeacher");
            queryClient.invalidateQueries("getFutureClassesForTeacher2");
        },
    });

    const onCancelUpcoming = (booking) => {
        if (booking?.booking_id) {
            getRefundAmount({
                booking_id: booking?.booking_id,
                type: 2,
            });
        } else {
            useToast("error", "Booking id does not exist");
        }
    };

    return (
        <>
            {selectedBooking ? (
                <DeclineModal
                    show={showDeclineModal}
                    handleClose={() => setShowDeclineModal(false)}
                    selectedBooking={selectedBooking}
                    cancellationData={cancellationData}
                />
            ) : null}

            {selectedBooking && showUserModal ? (
                <UserProfileModal
                    show={showUserModal}
                    handleClose={() => {
                        setSelectedBooking(null);
                        setShowUserModal(false);
                    }}
                    handleSendMessage={() => {
                        onSendMessage(selectedBooking?.user_id)
                        setShowUserModal(false);
                        setSelectedBooking(null);
                    }}
                    userId={selectedBooking?.user_id}
                    isInstructor
                />
            ) : null}

            <CancellationModal
                show={showCancelModal}
                handleClose={() => setShowCancelModal(false)}
                selectedBooking={selectedBooking}
                onCancel={() => {
                    setShowCancelModal(false);
                    onCancelUpcoming(selectedBooking);
                }}
                onClose={() => {
                    setShowCancelModal(false);
                }}
            />

            <Content>
                <Title>Upcoming Classes</Title>
                {futureClasses?.length ? (
                    <ClassList>
                        {futureClasses?.map((futureClasse) => (
                            <UpcomingClassCardInstructor
                                key={futureClasse.id}
                                upcomingClass={futureClasse}
                                acceptReschedule={(upcomingClass) => mutate(upcomingClass)}
                                rejectReschedule={(upcomingClass) => mutate(upcomingClass)}
                                sendMessage={(() => onSendMessage(futureClasse.user_id))}
                                onCancel={() => {
                                    setSelectedBooking(futureClasse);
                                    setShowCancelModal(true);
                                }}
                                onProfile={() => {
                                    setSelectedBooking(futureClasse);
                                    setShowUserModal(true)
                                }}
                            />
                        ))}
                    </ClassList>
                ) : (
                    <NoContent
                        title="No upcoming class found"
                        image={NoUpcomingClassesFound}
                        size={280}
                    />
                )}
            </Content>
        </>
    );
};

const Content = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  overflow: scroll;

  ${hideScrollBar}
`;

const ClassList = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.8rem;
`;

const Title = styled.p`
  font-size: ${fonts.sizes.medium};
  font-weight: 600;
  text-align: left;
  @media (max-width: 1024px) {
    display: none;
  }
`;
